// src/pages/rapim/RapimCalendar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Calendar, List, ChevronRight, ChevronLeft,
  Clock, MapPin, FileText, Loader2,
} from 'lucide-react';
import { Button, Card, Breadcrumb } from '../../components/ui';
import { RapatPimpinan } from '../../types';
import { rapimService } from '../../services/rapim';
import { useAuthStore } from '../../store/authStore';
import toast from 'react-hot-toast';

const jenisColors: Record<string, string> = {
  rutin: 'bg-teal-main',
  insidental: 'bg-warning',
  darurat: 'bg-danger',
};

const statusColors: Record<string, { bg: string; text: string }> = {
  dijadwalkan: { bg: 'bg-info/10', text: 'text-info' },
  berlangsung: { bg: 'bg-warning/10', text: 'text-warning' },
  selesai: { bg: 'bg-success/10', text: 'text-success' },
  dibatalkan: { bg: 'bg-danger/10', text: 'text-danger' },
};

const statusLabels: Record<string, string> = {
  dijadwalkan: 'Dijadwalkan',
  berlangsung: 'Berlangsung',
  selesai: 'Selesai',
  dibatalkan: 'Dibatalkan',
};

const namaBulan = [
  'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
  'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
];

const namaHari = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];

type StatusTab = 'semua' | 'dijadwalkan' | 'selesai' | 'dibatalkan';

type PaginationState = {
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
};

const defaultPagination: PaginationState = {
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
};

const RapimCalendar: React.FC = () => {
  const { user } = useAuthStore();
  const userRole = user?.role?.name || localStorage.getItem('user_role') || '';
  const canCreateRapat = ['admin', 'rektor'].includes(userRole.toLowerCase());

  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [rapatList, setRapatList] = useState<RapatPimpinan[]>([]);
  const [rapatCalendar, setRapatCalendar] = useState<RapatPimpinan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [activeStatus, setActiveStatus] = useState<StatusTab>('semua');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);
  const [stats, setStats] = useState({
    total_rapat: 0,
    dijadwalkan: 0,
    selesai: 0,
    dibatalkan: 0,
    total_keputusan: 0,
    keputusan_selesai: 0,
    avg_progress: 0,
  });

  const parsePaginatedResponse = (response: any) => {
    const payload = response?.data ?? response ?? {};
    const items = Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload)
        ? payload
        : [];

    const meta = {
      current_page: Number(payload?.current_page || payload?.meta?.current_page || 1),
      last_page: Number(payload?.last_page || payload?.meta?.last_page || 1),
      per_page: Number(payload?.per_page || payload?.meta?.per_page || 10),
      total: Number(payload?.total || payload?.meta?.total || items.length || 0),
    };

    return { items: items as RapatPimpinan[], meta };
  };

  const fetchStats = async () => {
    const statsRes = await rapimService.getStats();
    setStats((statsRes as any)?.data || statsRes);
  };

  const fetchList = async (page: number, status: StatusTab, showListLoader = true) => {
    if (showListLoader) setLoadingList(true);
    try {
      const rapatRes = await rapimService.getAll({
        page,
        per_page: 10,
        status: status === 'semua' ? undefined : status,
      });
      const { items, meta } = parsePaginatedResponse(rapatRes);
      setRapatList(items);
      setPagination(meta);
    } catch (err) {
      toast.error('Gagal memuat daftar RAPIM');
      console.error(err);
    } finally {
      if (showListLoader) setLoadingList(false);
    }
  };

  const fetchCalendar = async (date: Date) => {
    setLoadingCalendar(true);
    try {
      const rapatRes = await rapimService.getAll({
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        per_page: 200,
        page: 1,
      });
      const { items } = parsePaginatedResponse(rapatRes);
      setRapatCalendar(items);
    } catch (err) {
      toast.error('Gagal memuat kalender RAPIM');
      console.error(err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    const fetchDataAwal = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchStats(),
          fetchList(1, 'semua', false),
          fetchCalendar(new Date()),
        ]);
      } finally {
        setLoading(false);
      }
    };
    fetchDataAwal();
  }, []);

  useEffect(() => {
    if (view !== 'calendar') return;
    fetchCalendar(currentMonth);
  }, [view, currentMonth]);

  const handleTabChange = (status: StatusTab) => {
    if (activeStatus === status) return;
    setActiveStatus(status);
    setCurrentPage(1);
    fetchList(1, status, true);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.last_page || page === currentPage) return;
    setCurrentPage(page);
    fetchList(page, activeStatus, true);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    return { daysInMonth, firstDayOfMonth };
  };

  const getRapatForDay = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return rapatCalendar.filter((r) => {
      const rapatDate = new Date(r.tanggal);
      return (
        rapatDate.getDate() === day &&
        rapatDate.getMonth() === month &&
        rapatDate.getFullYear() === year
      );
    });
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const renderCalendar = () => {
    const { daysInMonth, firstDayOfMonth } = getDaysInMonth(currentMonth);
    const days = [];
    const today = new Date();

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 bg-neutral-bg/30 rounded-lg" />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dayRapat = getRapatForDay(day);
      const isToday =
        day === today.getDate() &&
        currentMonth.getMonth() === today.getMonth() &&
        currentMonth.getFullYear() === today.getFullYear();

      days.push(
        <div
          key={day}
          className={`h-24 p-1.5 rounded-lg border transition-colors ${isToday ? 'border-teal-main bg-teal-main/5' : 'border-neutral-border bg-white hover:border-teal-light'
            }`}
        >
          <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-teal-main' : 'text-neutral-text-secondary'}`}>
            {day}
          </div>
          <div className="space-y-1 overflow-hidden">
            {dayRapat.slice(0, 2).map((r) => (
              <Link key={r.id} to={`/rapim/${r.id}`}>
                <div className={`text-[10px] px-1 py-0.5 rounded truncate ${jenisColors[r.jenis]} text-white`}>
                  {r.judul}
                </div>
              </Link>
            ))}
            {dayRapat.length > 2 && (
              <div className="text-[10px] text-neutral-text-secondary px-1">
                +{dayRapat.length - 2} lainnya
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const pageItems = useMemo(() => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;
    if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);

    const pages: Array<number | '...'> = [1];
    if (current > 3) pages.push('...');
    for (let p = Math.max(2, current - 1); p <= Math.min(totalPages - 1, current + 1); p++) {
      pages.push(p);
    }
    if (current < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [pagination.current_page, pagination.last_page]);

  const jumlahTampilAwal = rapatList.length > 0
    ? (pagination.current_page - 1) * pagination.per_page + 1
    : 0;
  const jumlahTampilAkhir = rapatList.length > 0
    ? jumlahTampilAwal + rapatList.length - 1
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-teal-main mx-auto mb-3" />
          <p className="text-sm text-neutral-text-secondary">Memuat jadwal rapat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Rapat Pimpinan' },
      ]} />

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Rapat Pimpinan (RAPIM)</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Kelola jadwal dan notulensi rapat pimpinan</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center bg-neutral-bg rounded-lg p-0.5">
            <button
              onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'list' ? 'bg-white shadow text-teal-main' : 'text-neutral-text-secondary'}`}
            >
              <List className="w-4 h-4 inline mr-1" /> List
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'calendar' ? 'bg-white shadow text-teal-main' : 'text-neutral-text-secondary'}`}
            >
              <Calendar className="w-4 h-4 inline mr-1" /> Kalender
            </button>
          </div>
          {canCreateRapat && (
            <Link to="/rapim/buat">
              <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" />}>
                Buat Rapat
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-info/10 rounded-lg">
              <Calendar className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-xs text-neutral-text-secondary">Dijadwalkan</p>
              <p className="text-xl font-bold">{stats.dijadwalkan}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <FileText className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-xs text-neutral-text-secondary">Selesai</p>
              <p className="text-xl font-bold">{stats.selesai}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-danger/10 rounded-lg">
              <Plus className="w-5 h-5 text-danger rotate-45" />
            </div>
            <div>
              <p className="text-xs text-neutral-text-secondary">Dibatalkan</p>
              <p className="text-xl font-bold">{stats.dibatalkan}</p>
            </div>
          </div>
        </Card>
        <Card padding="sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Clock className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-xs text-neutral-text-secondary">Total Rapat</p>
              <p className="text-xl font-bold">{stats.total_rapat}</p>
            </div>
          </div>
        </Card>
      </div>

      {view === 'list' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'semua' as const, label: 'Semua Rapat', count: stats.total_rapat, activeClass: 'bg-neutral-bg text-neutral-800 border-neutral-300 shadow-sm' },
              { key: 'dijadwalkan' as const, label: 'Dijadwalkan', count: stats.dijadwalkan, activeClass: 'bg-info/10 text-info border-info/20' },
              { key: 'selesai' as const, label: 'Selesai', count: stats.selesai, activeClass: 'bg-success/10 text-success border-success/20' },
              { key: 'dibatalkan' as const, label: 'Dibatalkan', count: stats.dibatalkan, activeClass: 'bg-danger/10 text-danger border-danger/20' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${activeStatus === tab.key
                  ? tab.activeClass
                  : 'bg-white text-neutral-text-secondary border-neutral-border hover:border-neutral-text-secondary'
                  }`}
              >
                {tab.label}
                <span className={`inline-flex items-center justify-center min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] ${activeStatus === tab.key
                  ? 'bg-white text-current border border-current/10 shadow-sm'
                  : 'bg-neutral-bg text-neutral-text-primary'
                  }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {loadingList ? (
            <Card className="text-center py-10">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-teal-main" />
              <p className="text-sm text-neutral-text-secondary">Memuat data rapat...</p>
            </Card>
          ) : rapatList.length > 0 ? (
            <>
              <div className="space-y-3">
                {rapatList.map((r) => (
                  <Link key={r.id} to={`/rapim/${r.id}`}>
                    <Card hover className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-teal-main/5 rounded-xl flex flex-col items-center justify-center shrink-0">
                        <span className="text-lg font-black text-teal-main">
                          {new Date(r.tanggal).getDate()}
                        </span>
                        <span className="text-[10px] font-semibold uppercase text-teal-main">
                          {new Date(r.tanggal).toLocaleDateString('id-ID', { month: 'short' })}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${jenisColors[r.jenis]}`} />
                          <span className="text-xs font-semibold uppercase tracking-wider text-neutral-text-secondary">
                            {r.jenis}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${(statusColors[r.status] || statusColors.dijadwalkan).bg} ${(statusColors[r.status] || statusColors.dijadwalkan).text}`}>
                            {statusLabels[r.status] || 'Dijadwalkan'}
                          </span>
                        </div>
                        <h3 className="text-base font-bold text-neutral-text-primary truncate">{r.judul}</h3>
                        <div className="flex items-center gap-4 mt-1.5 text-xs text-neutral-text-secondary">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(r.tanggal).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {r.tempat && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {r.tempat}
                            </span>
                          )}
                        </div>
                      </div>

                      <ChevronRight className="w-5 h-5 text-neutral-text-secondary shrink-0" />
                    </Card>
                  </Link>
                ))}
              </div>

              <Card className="!py-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-xs text-neutral-text-secondary">
                    Menampilkan {jumlahTampilAwal}-{jumlahTampilAkhir} dari {pagination.total} rapat
                  </p>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="!text-xs !px-2.5"
                      disabled={pagination.current_page <= 1}
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                    >
                      Sebelumnya
                    </Button>
                    {pageItems.map((item, idx) => (
                      typeof item === 'number' ? (
                        <button
                          key={item}
                          type="button"
                          onClick={() => handlePageChange(item)}
                          className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border ${item === pagination.current_page
                            ? 'bg-teal-main text-white border-teal-main'
                            : 'bg-white text-neutral-text-secondary border-neutral-border hover:border-neutral-text-secondary'
                            }`}
                        >
                          {item}
                        </button>
                      ) : (
                        <span key={`ellipsis-${idx}`} className="px-1 text-xs text-neutral-text-secondary">...</span>
                      )
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="!text-xs !px-2.5"
                      disabled={pagination.current_page >= pagination.last_page}
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                    >
                      Selanjutnya
                    </Button>
                  </div>
                </div>
              </Card>
            </>
          ) : (
            <Card className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-neutral-border" />
              <p className="text-lg font-bold text-neutral-text-primary">Belum ada rapat</p>
              <p className="text-sm text-neutral-text-secondary mt-1">
                Tidak ada data rapat untuk status ini.
              </p>
            </Card>
          )}
        </div>
      )}

      {view === 'calendar' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h3 className="text-lg font-bold text-teal-dark min-w-[180px] text-center">
                {namaBulan[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </h3>
              <Button variant="outline" size="sm" onClick={nextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Hari Ini
            </Button>
          </div>

          {loadingCalendar ? (
            <div className="py-8 text-center">
              <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-teal-main" />
              <p className="text-sm text-neutral-text-secondary">Memuat kalender rapat...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-7 gap-2 mb-2">
                {namaHari.map((hari) => (
                  <div key={hari} className="text-center text-xs font-semibold text-neutral-text-secondary py-2">
                    {hari}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-2">
                {renderCalendar()}
              </div>

              <div className="flex items-center gap-4 mt-4 pt-4 border-t border-neutral-border">
                <span className="text-xs text-neutral-text-secondary">Keterangan:</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-teal-main" />
                    <span className="text-xs">Rutin</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-warning" />
                    <span className="text-xs">Insidental</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-danger" />
                    <span className="text-xs">Darurat</span>
                  </div>
                </div>
              </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default RapimCalendar;
